import { ReloadOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Upload,
} from "antd";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import moment from "moment";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import { FinanceAssetType, FinanceEntryType } from "../typeDefs";
import dayjs from "dayjs";

const cleanUndefined = <T,>(value: T): T => {
  if (Array.isArray(value)) return value.map((item) => cleanUndefined(item)) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, cleanUndefined(item)]),
    ) as T;
  }
  return value;
};

const categories: { categories: any; options: any } = {
  categories: {
    Revenue: [
      "Sales Revenue",
      "Service Revenue",
      "Interest Income",
      "Dividend Income",
      "Rental Income",
    ],
    Expenses: [
      "Cost of Goods Sold",
      "Salaries and Wages",
      "Shareholder Salary",
      "Rent Expense",
      "Utilities Expense",
      "Depreciation Expense",
      "Interest Expense",
      "Insurance Expense",
      "Advertising Expense",
      "Subscriptions",
      "Travel and Entertainment",
      "Miscellaneous Expenses",
      "Professional Fees",
      "Maintenance and Repairs",
      "Research and Development",
      "Training and Development",
      "Office Supplies Expense",
    ],
    Assets: [
      "Cash and Cash Equivalents",
      "Accounts Receivable",
      "Inventory",
      "Prepaid Expenses",
      "Property, Plant, and Equipment",
      "Intangible Assets",
      "Investments",
      "Office Fit-Out",
      "Other Current Assets",
    ],
    Liabilities: [
      "Accounts Payable",
      "GST Payable",
      "GST Payment",
      "Income Tax Payable",
      "Income Tax Payment",
      "Provisional Tax Payment",
      "PAYE/Withholding Tax Payment",
      "Short-term Debt",
      "Long-term Debt",
      "Loan Received",
      "Loan Repayment",
      "Accrued Liabilities",
      "Deferred Revenue",
      "Notes Payable",
    ],
    Equity: [
      "Common Stock",
      "Shareholder Drawings",
      "Owner Contribution",
      "Current Account Adjustment",
      "Preferred Stock",
      "Retained Earnings",
      "Additional Paid-in Capital",
      "Treasury Stock",
    ],
    "Non-Business": [
      "Personal / Other Account",
      "Cash Not Banked",
      "Duplicate / Imported In Error",
      "Not Company Spend",
      "Exclude Pending Deletion",
    ],
  },
  options: {
    "Income Statement": [
      "Sales Revenue",
      "Service Revenue",
      "Cost of Goods Sold",
      "Salaries and Wages",
      "Shareholder Salary",
      "Rent Expense",
      "Utilities Expense",
      "Depreciation Expense",
      "Interest Income",
      "Interest Expense",
      "Advertising Expense",
      "Net Income",
    ],
    "Balance Sheet": [
      "Cash and Cash Equivalents",
      "Accounts Receivable",
      "Inventory",
      "Prepaid Expenses",
      "Property, Plant, and Equipment",
      "Intangible Assets",
      "Investments",
      "Accounts Payable",
      "GST Payable",
      "GST Payment",
      "Income Tax Payable",
      "Income Tax Payment",
      "Provisional Tax Payment",
      "PAYE/Withholding Tax Payment",
      "Short-term Debt",
      "Long-term Debt",
      "Loan Received",
      "Loan Repayment",
      "Accrued Liabilities",
      "Deferred Revenue",
      "Notes Payable",
      "Common Stock",
      "Shareholder Drawings",
      "Owner Contribution",
      "Current Account Adjustment",
      "Preferred Stock",
      "Retained Earnings",
      "Additional Paid-in Capital",
      "Treasury Stock",
      "Personal / Other Account",
      "Cash Not Banked",
      "Duplicate / Imported In Error",
      "Not Company Spend",
      "Exclude Pending Deletion",
    ],
    "Cash Flow Statement": [
      "Net Income",
      "Depreciation Expense",
      "Accounts Receivable",
      "Inventory",
      "Accounts Payable",
      "Accrued Liabilities",
      "Cash from Operating Activities",
      "Cash from Investing Activities",
      "Cash from Financing Activities",
    ],
    "Statement of Changes in Equity": [
      "Common Stock",
      "Shareholder Drawings",
      "Preferred Stock",
      "Retained Earnings",
      "Additional Paid-in Capital",
      "Treasury Stock",
      "Net Income",
      "Dividends",
    ],
  },
};

export const Finances = ({
  activeItem,
}: {
  activeItem: FinanceEntryType | null;
}) => {
  const [activeCategory, setActiveCategory] = useState<string>("Revenue");
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [gstSearch, setGstSearch] = useState<string>("");
  const [isGstRegistered, setIsGstRegistered] = useState<boolean>(true);
  const [isGstIncluded, setIsGstIncluded] = useState<boolean>(true);
  const [entryDate, setEntryDate] = useState<number>(Date.now());
  const [vendors, setVendors] = useState<
    Array<{ vendor: string; id: string; gstNumber: string }>
  >([]);
  const [financeAssets, setFinanceAssets] = useState<FinanceAssetType[]>([]);

  const [entryForm] = Form.useForm();
  const [depreciationForm] = Form.useForm();
  const [apportionmentForm] = Form.useForm();

  useEffect(() => {
    if (!activeItem) {
      return;
    }

    setEntryDate(activeItem?.date);
    setImagePath(activeItem?.image);
    setIsGstRegistered(activeItem?.gstRegistered ?? true);
    setIsGstIncluded(activeItem?.gstIncluded ?? true);
    setActiveCategory(activeItem?.category || "Revenue");

    entryForm.setFieldsValue({
      businessUseRate: activeItem.businessUseRate ?? activeItem.apportionRate ?? 100,
      originalAmount: activeItem.originalAmount ?? activeItem.amount,
      apportionMethod: activeItem.apportionMethod || "Principal Method",
      apportionRate: activeItem.apportionRate ?? activeItem.businessUseRate ?? 100,
      apportionmentSource: activeItem.apportionmentSource || "Business Account",
      yearEndTreatment: activeItem.yearEndTreatment || undefined,
      isHomeOfficeExpense: activeItem.isHomeOfficeExpense || false,
      isPersonalCardSpend: activeItem.isPersonalCardSpend || false,
      ...activeItem,
    });
    depreciationForm.setFieldsValue(activeItem);
    apportionmentForm.setFieldsValue({
      apportionMethod: activeItem.apportionMethod || "Principal Method",
      apportionRate: activeItem.apportionRate ?? activeItem.businessUseRate ?? 100,
    });
  }, [activeItem, depreciationForm, apportionmentForm]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "financeAssets"),
      (snapDoc) => {
        setFinanceAssets(
          snapDoc.docs
            .map((item) => ({ ...item.data(), id: item.id } as FinanceAssetType))
            .filter((item) => !item.isDeleted)
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );
      },
      (err) => console.error(err),
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "vendors"),
      (snapDoc) => {
        setVendors(
          snapDoc.docs.map(
            (doc) =>
              ({ ...doc.data(), id: doc.id } as {
                vendor: string;
                id: string;
                gstNumber: string;
              })
          )
        );
      },
      (err) => {
        console.error(err);
      }
    );
    return unsub;
  }, []);

  const buildEntryPayload = (values: any, id: string) => {
    const businessUseRate = Number(values.businessUseRate ?? values.apportionRate ?? 100);
    const originalAmount = Number(values.originalAmount || values.amount || 0);
    const isSplitBusinessSpend =
      ["Expenses", "Assets"].includes(values.category) && businessUseRate >= 0 && businessUseRate < 100;
    const claimAmount =
      isSplitBusinessSpend && originalAmount > 0
        ? originalAmount * (businessUseRate / 100)
        : Number(values.amount || 0);

    const gstApplicable = !["Equity", "Non-Business", "Liabilities"].includes(values.category);

    return {
      ...values,
      amount: claimAmount,
      originalAmount: originalAmount || claimAmount,
      businessUseRate,
      apportionRate: businessUseRate,
      apportionMethod:
        values.category === "Expenses" || values.category === "Assets"
          ? businessUseRate === 0
            ? "None"
            : businessUseRate === 100
            ? "Principal Method"
            : values.apportionMethod || "Apportionment Method"
          : null,
      apportionmentSource: values.apportionmentSource || "Business Account",
      gstIncluded: gstApplicable ? isGstIncluded : false,
      gstRegistered: gstApplicable ? isGstRegistered : false,
      gstNumber: gstApplicable && values.gstNumber ? values.gstNumber : null,
      date: entryDate,
      id,
      image: imagePath,
      isDeleted: false,
      receipt: imagePath ? true : false,
      isHomeOfficeExpense: values.isHomeOfficeExpense || isSplitBusinessSpend,
      isPersonalCardSpend:
        values.apportionmentSource === "Personal Account" ? false : values.isPersonalCardSpend || businessUseRate === 0,
      yearEndTreatment:
        values.apportionmentSource === "Personal Account"
          ? values.yearEndTreatment || null
          : values.yearEndTreatment || (businessUseRate === 0 ? "Shareholder Salary" : null),
      financeAssetId: values.financeAssetId || null,
      financeAssetName: values.financeAssetId
        ? financeAssets.find((asset) => asset.id === values.financeAssetId)?.name || null
        : null,
      financeAssetAssignedAt: values.financeAssetId
        ? activeItem?.financeAssetId === values.financeAssetId
          ? activeItem?.financeAssetAssignedAt || Date.now()
          : Date.now()
        : null,
    } as FinanceEntryType;
  };

  const applyApportionmentToForm = () => {
    const values = entryForm.getFieldsValue();
    const originalAmount = Number(values.originalAmount || values.amount || 0);
    const businessUseRate = Number(values.businessUseRate ?? values.apportionRate ?? 100);
    const claimAmount = originalAmount * (businessUseRate / 100);

    entryForm.setFieldsValue({
      amount: Number.isFinite(claimAmount) ? Number(claimAmount.toFixed(2)) : values.amount,
      apportionRate: businessUseRate,
      apportionMethod:
        businessUseRate === 0
          ? "None"
          : businessUseRate === 100
          ? "Principal Method"
          : "Apportionment Method",
      isHomeOfficeExpense: businessUseRate < 100,
      isPersonalCardSpend: businessUseRate === 0,
      yearEndTreatment:
        values.apportionmentSource === "Personal Account"
          ? values.yearEndTreatment
          : businessUseRate < 100
          ? "Shareholder Salary"
          : values.yearEndTreatment,
    });
  };

  const markAsPersonalDrawing = () => {
    setActiveCategory("Equity");
    setIsGstIncluded(false);
    setIsGstRegistered(false);
    entryForm.setFieldsValue({
      category: "Equity",
      subCategory: "Shareholder Drawings",
      gstNumber: null,
      businessUseRate: 0,
      apportionRate: 0,
      apportionMethod: "None",
      isPersonalCardSpend: true,
      yearEndTreatment: "Shareholder Salary",
    });
  };

  const handleAdEntry = () => {
    entryForm
      .validateFields()
      .then((values) => {
        console.log({ values });
        const newId = nanoid();
        const servicesDoc = doc(db, `/finances/${newId}`);
        setDoc(servicesDoc, cleanUndefined(buildEntryPayload(values, newId)))
          .then(() => message.success("Saved new service."))
          .catch((err) => console.error(err));

        return values;
      })
      .then((values) => {
        if (!vendors.map((item) => item.vendor).includes(values.vendor)) {
          const vendorId = nanoid();
          const vendorDoc = doc(db, `/vendors/${vendorId}`);
          setDoc(vendorDoc, cleanUndefined({
            id: vendorId,
            vendor: values.vendor,
            gstNumber: values.gstNumber || null,
          }))
            .then(() => message.success("Vendors saved"))
            .catch((err) => console.error(err));
        }
      })
      .then(() => {
        entryForm.resetFields();
        setGstSearch("");
      })
      .catch((err) => {
        message.error(err.message);
        console.error(err);
      });
  };

  const handleEditEntry = () => {
    entryForm
      .validateFields()
      .then((values) => {
        updateDoc(doc(db, `finances/${activeItem?.id}`), cleanUndefined(buildEntryPayload(values, activeItem?.id || "")))
          .then(() => message.success("Saved new service."))
          .catch((err) => console.error(err));
      })
      .catch((err) => {
        message.error(err.message);
        console.error(err);
      });
  };

  const handleDepreciation = () => {
    depreciationForm
      .validateFields()
      .then((values) => {
        updateDoc(doc(db, `finances/${activeItem?.id}`), cleanUndefined({
          ...values,
        } as FinanceEntryType))
          .then(() => message.success("Saved depreciation."))
          .catch((err) => console.error(err));
      })
      .catch((err) => console.error(err));
  };

  const handleApportionment = () =>
    apportionmentForm
      .validateFields()
      .then((values) => {
        updateDoc(doc(db, `finances/${activeItem?.id}`), cleanUndefined({
          apportionMethod: values.apportionMethod || null,
          apportionRate:
            values.apportionMethod === "Principal Method"
              ? 100
              : values.apportionMethod === "None"
              ? 0
              : values.apportionRate ?? null,
        } as FinanceEntryType))
          .then(() => message.success("Saved apportionment method."))
          .catch((err) => console.error(err));
      })
      .catch((err) => console.error(err));

  const handleDeleteEntry = () => {
    updateDoc(doc(db, `finances/${activeItem?.id}`), {
      isDeleted: true,
    } as FinanceEntryType)
      .then(() => message.success("Deleted entry."))
      .catch((err) => console.error(err));
  };

  return (
    <div className="finance-entry-form text-white">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <h1
          style={{
            textAlign: "center",
            color: "rgb(100,100,100)",
            fontFamily: "Oxanium",
            marginBottom: "3rem",
          }}
        >
          {activeItem ? "Edit Financial Entry" : "Add Financial Entry"}
        </h1>
        <div style={{ paddingBottom: "1rem" }}>
          {!activeItem && (
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setEntryDate(Date.now());
                setImagePath(null);
                setIsGstRegistered(true);
                setIsGstIncluded(true);
                entryForm.resetFields();
              }}
            />
          )}
        </div>
        <Form
          layout="vertical"
          style={{ width: "100%", maxWidth: "1000px" }}
          form={entryForm}
          initialValues={{
            gstNumber: "",
            businessUseRate: 100,
            apportionRate: 100,
            apportionMethod: "Principal Method",
            apportionmentSource: "Business Account",
            originalAmount: activeItem?.originalAmount ?? activeItem?.amount,
            ...activeItem,
          }}
        >
          <Form.Item label="Transaction date">
            <DatePicker
              defaultValue={dayjs(activeItem?.date)}
              onChange={(e) => {
                if (!e) return;
                setEntryDate(e.toDate().getTime());
              }}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true }]}
          >
            <Input placeholder="Description" />
          </Form.Item>
          <Form.Item name="gstNumber" label="GST number">
            <Input
              placeholder="GST Number"
              onChange={(e) => setGstSearch(e.target.value)}
            />
          </Form.Item>
          {gstSearch.length > 2 && vendors.length > 0 && (
            <div>
              {vendors
                .filter((item) => item.gstNumber?.includes(gstSearch))
                .map((item, idx) => (
                  <p
                    style={{ color: "rgb(200,200,200)" }}
                    onClick={() =>
                      entryForm.setFieldsValue({
                        gstNumber: item.gstNumber,
                        vendor: item.vendor,
                      })
                    }
                    key={idx}
                  >
                    {item.vendor}
                  </p>
                ))}
            </div>
          )}
          <Form.Item
            name="vendor"
            label="Other party / vendor"
            rules={[{ required: true }]}
          >
            <Input placeholder="Other Party" />
          </Form.Item>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
            }}
          >
            <Form.Item name="originalAmount" label="Full bank/card amount">
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Full spend"
              />
            </Form.Item>
            <Form.Item
              name={"amount"}
              label="Business claim amount"
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Claim amount"
              />
            </Form.Item>
            <Form.Item name="businessUseRate" label="Business claim %">
              <InputNumber
                min={0}
                max={100}
                style={{ width: "100%" }}
                placeholder="100"
              />
            </Form.Item>
          </div>

          <Form.Item shouldUpdate>
            {() => {
              const fullAmount = Number(
                entryForm.getFieldValue("originalAmount") ||
                  entryForm.getFieldValue("amount") ||
                  0,
              );
              const businessUseRate = Number(
                entryForm.getFieldValue("businessUseRate") ?? 100,
              );
              const personalAmount = Math.max(
                0,
                fullAmount - fullAmount * (businessUseRate / 100),
              );
              return (
                <div style={{ color: "#F6B34B", fontSize: ".85rem" }}>
                  Personal / shareholder drawing portion:{" "}
                  {`$${personalAmount.toFixed(2)}`}
                </div>
              );
            }}
          </Form.Item>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <Button
              size="small"
              onClick={() =>
                entryForm.setFieldsValue({
                  businessUseRate: 100,
                  apportionRate: 100,
                  apportionMethod: "Principal Method",
                  apportionmentSource: "Business Account",
                  isHomeOfficeExpense: false,
                  isPersonalCardSpend: false,
                })
              }
            >
              100% business
            </Button>
            <Button size="small" onClick={applyApportionmentToForm}>
              Apply split %
            </Button>
            <Button size="small" onClick={markAsPersonalDrawing}>
              100% personal / shareholder draw
            </Button>
          </div>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Category"
              onChange={(e) => {
                setActiveCategory(e);
                entryForm.setFieldsValue({
                  subCategory: categories.categories[e]?.[0],
                });
              }}
            >
              {Object.keys(categories.categories).map((key, idx) => {
                return (
                  <Select.Option key={idx} value={key}>
                    {key}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item name="financeAssetId" label="Figurative asset container">
            <Select
              allowClear
              showSearch
              placeholder="Optional parent asset container"
              optionFilterProp="label"
              options={financeAssets.map((asset) => ({ value: asset.id, label: asset.name }))}
            />
          </Form.Item>
          <Form.Item
            name="subCategory"
            label="Subcategory"
            rules={[{ required: true }]}
          >
            <Select placeholder="Sub Category">
              {(categories.categories[activeCategory] || []).map(
                (key: string, idx: number) => {
                  return (
                    <Select.Option key={idx} value={key}>
                      {key}
                    </Select.Option>
                  );
                },
              )}
            </Select>
          </Form.Item>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 8,
            }}
          >
            <Form.Item>
              <Checkbox
                style={{ color: "rgb(180,180,180)" }}
                onChange={() => setIsGstIncluded(!isGstIncluded)}
                checked={isGstIncluded}
              >
                Includes GST
              </Checkbox>
            </Form.Item>
            <Form.Item>
              <Checkbox
                style={{ color: "rgb(180,180,180)" }}
                onChange={() => setIsGstRegistered(!isGstRegistered)}
                checked={isGstRegistered}
              >
                GST Registered
              </Checkbox>
            </Form.Item>
            <Form.Item name="isHomeOfficeExpense" valuePropName="checked">
              <Checkbox style={{ color: "rgb(180,180,180)" }}>
                Split/apportioned business-account spend
              </Checkbox>
            </Form.Item>
            <Form.Item name="isPersonalCardSpend" valuePropName="checked">
              <Checkbox style={{ color: "rgb(180,180,180)" }}>
                Personal spend / shareholder drawing
              </Checkbox>
            </Form.Item>
          </div>

          <Form.Item name="apportionMethod" label="Apportionment method">
            <Select
              options={[
                {
                  value: "Principal Method",
                  label: "100% business / principal method",
                },
                {
                  value: "Apportionment Method",
                  label: "Split business + personal",
                },
                { value: "None", label: "0% business / personal only" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="apportionmentSource"
            label="Where was the full transaction paid from?"
          >
            <Select
              options={[
                {
                  value: "Business Account",
                  label:
                    "Business account/card - personal portion becomes shareholder drawings",
                },
                {
                  value: "Personal Account",
                  label:
                    "Personal account/card - record business claim only, no shareholder drawing",
                },
              ]}
            />
          </Form.Item>
          <Form.Item shouldUpdate>
            {() =>
              entryForm.getFieldValue("apportionmentSource") ===
              "Personal Account" ? (
                <div
                  style={{
                    color: "#9ca3af",
                    fontSize: ".85rem",
                    marginTop: -8,
                  }}
                >
                  Personal-account apportionment: only the business claim is
                  recorded in the company accounts. The unclaimed personal
                  portion will not be added to shareholder drawings or main-bank
                  reconciliation.
                </div>
              ) : null
            }
          </Form.Item>
          <Form.Item
            name="yearEndTreatment"
            label="Year-end treatment for personal portion"
          >
            <Select
              allowClear
              placeholder="Optional"
              options={[
                {
                  value: "Shareholder Salary",
                  label: "Treat as shareholder salary at year end",
                },
                {
                  value: "Shareholder Drawings",
                  label: "Keep as shareholder drawings",
                },
              ]}
            />
          </Form.Item>
          <Upload
            beforeUpload={(upload) => {
              const storageRef = ref(storage, `receipts/${upload.name}`);
              if (upload.type === "image/jpeg" || upload.type === "image/png") {
                uploadBytes(storageRef, upload)
                  .then((snapshot) => {
                    message.success("Uploaded file!");
                  })
                  .then(() => getDownloadURL(storageRef))
                  .then((url) => {
                    setImagePath(url);
                  })
                  .catch((err) => console.error(err));
              }
            }}
          >
            <Button>Select File</Button>
          </Upload>
          <div>
            {activeItem?.image && (
              <Image src={activeItem?.image} alt="Record.img" />
            )}
          </div>
          {activeItem ? (
            <>
              <Button
                style={{ marginTop: ".25rem" }}
                onClick={() => {
                  handleEditEntry();
                }}
              >
                Update entry
              </Button>
              <Popconfirm
                title="Are you sure?"
                onConfirm={() => {
                  handleDeleteEntry();
                }}
              >
                <Button style={{ marginTop: ".25rem" }}>Delete entry</Button>
              </Popconfirm>
            </>
          ) : (
            <Button
              style={{ marginTop: ".25rem" }}
              onClick={() => {
                handleAdEntry();
              }}
            >
              Save entry
            </Button>
          )}
        </Form>
        {activeItem && activeItem.category === "Assets" && (
          <div
            style={{
              marginTop: "1rem",
              border: "1px solid grey",
              borderRadius: ".5rem",
              width: "350px",
              padding: ".5rem",
            }}
          >
            <div>Depreciation of Assets</div>
            <Form form={depreciationForm} style={{ width: "100%" }}>
              <Form.Item name="depreciationMethod">
                <Select
                  options={[
                    { value: "Straight Line", label: "Straight Line" },
                    { value: "Diminishing Value", label: "Diminishing Value" },
                  ]}
                />
              </Form.Item>
              <Form.Item name="depreciationRate">
                <InputNumber placeholder="%" />
              </Form.Item>
            </Form>
            <Button
              onClick={() => {
                handleDepreciation();
              }}
            >
              Save
            </Button>
          </div>
        )}
        {activeItem &&
          (activeItem.category === "Expenses" ||
            activeItem.category === "Assets") && (
            <div
              style={{
                marginTop: "1rem",
                border: "1px solid grey",
                borderRadius: ".5rem",
                width: "350px",
                padding: ".5rem",
              }}
            >
              <div>Method of GST claim for expenses</div>
              <Form form={apportionmentForm} style={{ width: "100%" }}>
                <Form.Item name="apportionMethod">
                  <Select
                    options={[
                      { value: "Principal Method", label: "Principal Method" },
                      {
                        value: "Apportionment Method",
                        label: "Apportionment Method",
                      },
                      {
                        value: "None",
                        label: null,
                      },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="apportionRate">
                  <InputNumber
                    max={100}
                    min={0}
                    style={{ width: "100%" }}
                    placeholder="%"
                  />
                </Form.Item>
              </Form>
              <Button
                onClick={() => {
                  handleApportionment();
                }}
              >
                Save
              </Button>
            </div>
          )}
      </div>
    </div>
  );
};
